<script lang="ts" setup>
import { watch } from 'vue'
import { useUserStore } from '@/stores'
import {
  createShowPaymentMethodSelector,
  usePurchase,
} from '@/composables/payment'
import PaymentMethodSelector from '@/composables/payment/components/PaymentMethodSelector.flipnovel.vue'
import { useAxonViewItem } from '@/composables/axon/useAxonViewItem'
import { useFbqAddToCart } from '@/composables/fbq/useFbqAddToCart'
import { theme as brandTheme } from '@/config/brands'

const props = defineProps<{
  config: any
}>()

const user = useUserStore()

// 使用充值 composable（支持多支付方式）
const {
  templateProducts,
  PayTemplate,
  currentPaymentMethod,
  availablePaymentMethods,
  selectPaymentMethod,
} = usePurchase('store')

// 是否显示支付方式选择器
const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

// Axon view_item 上报（商品加载 + 支付方式切换均触发，Set 内部去重）
const { reportViewItem } = useAxonViewItem()
watch(templateProducts, (val) => {
  if (val.length)
    reportViewItem(val, currentPaymentMethod.value, 'Coins Package')
})

const { reportAddToCart } = useFbqAddToCart()
watch(templateProducts, (val) => {
  for (const p of val)
    reportAddToCart(p.productId, 'Coins Package', p.price ?? 0)
})

const payItemActiveBg = brandTheme.bgCard || '#1C1C1E'
const isLightPaymentSelector = !!props.config.theme?.isLightPaymentSelector
const paymentStickyGradient = props.config.theme?.paymentStickyGradient || ''
const payColors = props.config.theme?.colors
const tagGradient = payColors?.tagGradient || ''
</script>

<template>
  <div class="min-h-100dvh bg-n-bg-global">
    <BasicNavbar class="color-n-text-2" />

    <!-- Cover -->
    <div class="pay-cover-shell mb-24px mx-10px" :class="{ 'bg-n-bg-card b-rd-2': !isLightPaymentSelector }">
      <div class="pay-cover" :class="{ 'border border-n-border-primary': !isLightPaymentSelector }">
        <div class="pt-18px px-26px">
          <p class="flex items-center space-x-5px">
            <span class="text-18px text-n-primary font-bold">{{ user.$state.coinBalance }}</span>
            <span class="relative text-12px text-n-text-2">{{ config.texts.coins }}</span>
          </p>
        </div>

        <div class="pay-template pt-2px pb-0px">
          <!-- 充值 -->
          <div
            v-for="(item) in templateProducts" :key="item.productId"
            class="pay-template-info relative flex items-center justify-between mt-10px mx-10px px-16px py-16px b-rd-2 bg-n-bg-global border border-n-border-primary transition-all duration-200 active:border-n-primary"
            @click="PayTemplate(item)"
          >
            <i v-if="item.cornerTip === 1 && item.marketingText" class="absolute top-0 left-0 px-9px text-white text-10px font-not-italic whitespace-nowrap" :style="{ background: tagGradient, borderRadius: '12px 12px 12px 0' }">{{ item.marketingText }}</i>
            <div class="min-w-0 flex flex-1 items-center gap-15px pr-12px">
              <div class="shrink-0">
                <img class="w-24px h-24px" :src="config.resources.goldCoinIcon">
              </div>
              <div class="min-w-0 flex-1">
                <div>
                  <strong class="text-18px text-n-text-1">{{ item.coinsBuy }}</strong>
                  <span class="ml-5px text-14px text-n-text-2">{{ config.texts.coins }}</span>
                </div>
                <div class="flex items-center">
                  <span class="text-14px text-n-primary">+{{ item.coinsBonus }} {{ config.texts.coins }}</span>
                </div>
              </div>
            </div>
            <div class="shrink-0 text-right">
              <strong class="text-18px text-n-text-1">$ {{ item.price }}</strong>
              <p v-if="item.cornerTip" class="text-12px text-n-text-2">
                +{{ parseFloat((item.coinsBonus / item.coinsBuy * 100).toFixed(2)) }}% {{ config.texts.coins }}
              </p>
            </div>
          </div>
        </div>

        <div :class="`${showPaymentMethodSelector ? 'pb-96px' : 'pb-20px'} px-16px text-10px text-n-text-3`">
          <p class="text-12px mt-6px">
            {{ config.texts.tips }}
          </p>
          <p class="mt-6px">
            {{ config.texts.tip1 }}
          </p>
          <p class="mt-6px">
            {{ config.texts.tip2 }}
          </p>
          <p class="mt-6px">
            {{ config.texts.tip3 }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="showPaymentMethodSelector" class="payment-method-sticky" :style="{ background: paymentStickyGradient }">
      <div class="payment-method-sticky-inner">
        <PaymentMethodSelector
          variant="large"
          :dark="!isLightPaymentSelector"
          :model-value="currentPaymentMethod"
          :available-methods="availablePaymentMethods"
          @update:model-value="selectPaymentMethod"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pay-cover-shell {
  padding-top: 16px;
  overflow: hidden;
}

.pay-cover {
  min-height: 100%;
  border-radius: inherit;
}

.pay-template-info:active {
  background: v-bind(payItemActiveBg);
}

.payment-method-sticky {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  pointer-events: none;
}

.payment-method-sticky-inner {
  max-width: 750px;
  margin: 0 auto;
  min-height: 120px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0 10px calc(14px + env(safe-area-inset-bottom));
  pointer-events: auto;
}
</style>
